import { IAddItemBase, IProductPricesDetails } from "@interfaces";
import { ICartItemData } from "@interfaces";

/**
 * Working out how to add tickets the cart may already hold.
 *
 * `POST /cart/items/v2` refuses a booking that is already there — "Item already
 * exists" — rather than adding to it, so asking for two more adults on a product
 * already in the cart failed outright. Pressing Add to Cart or Book Now again
 * should raise the quantity instead, which means splitting the request: the
 * fares already present become quantity updates on their existing rows, and
 * anything genuinely new is still posted.
 */

/** Product, fare, date and departure time together identify a cart line. */
const lineKey = (
    productId: string | number,
    ppdId: string | number | null | undefined,
    bookingDate: string | undefined,
    timeId: string | null | undefined,
): string => `${productId}#${ppdId ?? ""}#${bookingDate ?? ""}#${timeId ?? ""}`;

export const cartRowKey = (row: ICartItemData): string =>
    lineKey(row.tdms_product_id, row.product_price_details_id, row.booking_date, row.time_id);

export interface ICartQuantityUpdate {
    row: ICartItemData;
    newQuantity: number;
}

export interface ICartAdditionPlan {
    /** Existing rows whose quantity should go up. */
    updates: ICartQuantityUpdate[];
    /** The part of the request that is not in the cart yet, or null if none is. */
    remainder: IAddItemBase | null;
}

export const planCartAddition = (item: IAddItemBase, rows: ICartItemData[]): ICartAdditionPlan => {
    const byKey = new Map(rows.map((row) => [cartRowKey(row), row]));
    const updates = new Map<string, ICartQuantityUpdate>();
    const remainderDetails: IProductPricesDetails[] = [];

    for (const detail of item.productPricesDetails ?? []) {
        const newEntries = [];

        for (const entry of detail.quantityDetails ?? []) {
            const key = lineKey(
                item.tdmsProductId,
                detail.productPricesDetailsId,
                entry.bookingDate,
                entry.timeId,
            );
            const row = byKey.get(key);

            if (!row) {
                newEntries.push(entry);
                continue;
            }

            // Accumulated, so asking twice for the same fare in one request adds
            // both lots rather than the last one winning.
            const already = updates.get(key);
            updates.set(key, {
                row,
                newQuantity: (already?.newQuantity ?? row.booking_quantity) + entry.quantity,
            });
        }

        if (newEntries.length > 0) {
            remainderDetails.push({ ...detail, quantityDetails: newEntries });
        }
    }

    return {
        updates: Array.from(updates.values()),
        remainder:
            remainderDetails.length > 0
                ? { ...item, productPricesDetails: remainderDetails }
                : null,
    };
};
